import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { SERVICE_OPTIONS, AREAS, CONTACT } from "../../lib/site";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", phone: "", service: "", location: "", property_type: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please share your name, email and service of interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Thank you — your request has been received.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-ocean-teal py-3 font-sans text-black placeholder:text-charcoal/50 focus:outline-none focus:border-ocean-teal transition-colors";

  return (
    <section id="quote" className="relative bg-warm-white pt-10 pb-24 md:pt-16 md:pb-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><Overline>Request a Quote</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl text-ocean-teal leading-tight">
              {"Let's prepare your space beautifully."}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-sans font-light text-charcoal/70 max-w-md leading-relaxed">
              {"Tell us a little about your home or property and we'll craft a tailored service plan with timeless attention to detail."}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 space-y-4">
              <a href={`mailto:${CONTACT.email}`} className="link-underline block font-serif text-xl text-ocean-teal w-fit">{CONTACT.email}</a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="link-underline block font-serif text-xl text-ocean-teal w-fit">{CONTACT.phone}</a>
              <p className="font-sans text-sm text-charcoal/60">Based in {CONTACT.base}</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          {done ? (
            <div data-testid="quote-success" className="h-full min-h-[320px] flex flex-col items-center justify-center text-center border border-border-stone rounded-sm p-12 bg-warm-white">
              <span className="font-serif text-3xl text-ocean-teal">Thank you.</span>
              <p className="mt-4 font-sans font-light text-charcoal/70 max-w-sm">
                Your request has arrived. A member of our boutique team will be in touch shortly to
                craft your tailored service plan.
              </p>
              <button onClick={() => setDone(false)} className="mt-8 text-sm text-antique-gold link-underline">Send another request</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <input data-testid="quote-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="quote-email" type="email" className={field} placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <input data-testid="quote-phone" className={field} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                <input data-testid="quote-property" className={field} placeholder="Property type (e.g. villa, office)" value={form.property_type} onChange={(e) => set("property_type", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <Select value={form.service} onValueChange={(v) => set("service", v)}>
                  <SelectTrigger data-testid="quote-service" className="w-full bg-transparent border-0 border-b border-ocean-teal rounded-none px-0 py-3 h-auto font-sans text-black focus:ring-0 focus:border-ocean-teal">
                    <SelectValue placeholder="Service of interest" />
                  </SelectTrigger>
                  <SelectContent className="bg-warm-white border-border-stone">
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} className="font-sans">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.location} onValueChange={(v) => set("location", v)}>
                  <SelectTrigger data-testid="quote-location" className="w-full bg-transparent border-0 border-b border-ocean-teal rounded-none px-0 py-3 h-auto font-sans text-black focus:ring-0 focus:border-ocean-teal">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent className="bg-warm-white border-border-stone">
                    {AREAS.map((a) => (
                      <SelectItem key={a} value={a} className="font-sans">{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <textarea data-testid="quote-message" rows={4} className={`${field} resize-none`} placeholder="Tell us about your space..." value={form.message} onChange={(e) => set("message", e.target.value)} />
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit"
                className="rounded-full bg-ocean-teal px-10 py-4 text-sm font-medium tracking-wide text-warm-white hover:bg-antique-gold transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Request"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
